import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Building2,
  Calendar,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Edit,
  Trash2,
  CheckCircle,
  FileText,
  Download,
  X,
  Star,
  Hotel,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelHotelBooking = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hotels, setHotels] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editHotel, setEditHotel] = useState(null);
  const [workflowHotel, setWorkflowHotel] = useState(null);
  const [voucherHotel, setVoucherHotel] = useState(null);
  const [deleteHotel, setDeleteHotel] = useState(null);

  // Form State
  const initialForm = {
    employee_name: '',
    department: 'General',
    hotel_name: '',
    city: 'Bengaluru',
    check_in: new Date().toISOString().split('T')[0],
    check_out: new Date(Date.now() + 86400000 * 3).toISOString().split('T')[0],
    number_of_rooms: 1,
    guests: 1,
    room_type: 'Deluxe Executive King',
    meals: 'Breakfast Included',
    corporate_rate: 6500,
    booking_amount: 19500,
    vendor_name: 'Taj Hotels & Resorts',
    remarks: '',
  };

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    fetchHotels();
  }, [statusFilter, cityFilter]);

  const fetchHotels = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/travel-desk/hotels?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      if (cityFilter !== 'all') url += `city=${cityFilter}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;

      const { data } = await axios.get(url, { withCredentials: true });
      setHotels(data.hotels || []);
    } catch (err) {
      toast.error('Failed to load hotel bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHotel = async (e) => {
    e.preventDefault();
    if (!formData.hotel_name || !formData.city) {
      toast.error('Please enter hotel name and city');
      return;
    }

    try {
      if (editHotel) {
        await axios.put(`${API_URL}/travel-desk/hotels/${editHotel._id}`, formData, { withCredentials: true });
        toast.success('Hotel booking updated successfully!');
      } else {
        await axios.post(`${API_URL}/travel-desk/hotels`, formData, { withCredentials: true });
        toast.success('New Hotel booking created successfully!');
      }
      setShowCreateModal(false);
      setEditHotel(null);
      setFormData(initialForm);
      fetchHotels();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save hotel booking');
    }
  };

  const handleWorkflowAction = async (hotelId, action, extraPayload = {}) => {
    try {
      await axios.post(
        `${API_URL}/travel-desk/hotels/${hotelId}/action`,
        { action, ...extraPayload },
        { withCredentials: true }
      );
      toast.success(`Action '${action}' processed successfully`);
      setWorkflowHotel(null);
      fetchHotels();
    } catch (err) {
      toast.error('Failed to update hotel workflow stage');
    }
  };

  const handleDeleteHotel = async (hotelId) => {
    try {
      await axios.delete(`${API_URL}/travel-desk/hotels/${hotelId}`, { withCredentials: true });
      toast.success('Hotel booking deleted');
      setDeleteHotel(null);
      fetchHotels();
    } catch (err) {
      toast.error('Failed to delete hotel booking');
    }
  };

  const openEdit = (h) => {
    setEditHotel(h);
    setFormData({
      employee_name: h.employee_name || '',
      department: h.department || 'General',
      hotel_name: h.hotel_name || '',
      city: h.city || 'Bengaluru',
      check_in: h.check_in || '',
      check_out: h.check_out || '',
      number_of_rooms: h.number_of_rooms || 1,
      guests: h.guests || 1,
      room_type: h.room_type || 'Deluxe Executive King',
      meals: h.meals || 'Breakfast Included',
      corporate_rate: h.corporate_rate || 6500,
      booking_amount: h.booking_amount || 0,
      vendor_name: h.vendor_name || 'Taj Hotels',
      remarks: h.remarks || '',
    });
    setShowCreateModal(true);
  };

  const exportCSV = () => {
    const headers = ['Booking Ref', 'Employee', 'Hotel Name', 'City', 'Check-in', 'Check-out', 'Room Type', 'Amount', 'Vendor', 'Status'];
    const rows = filteredHotels.map(h => [
      h.booking_ref, h.employee_name, `"${h.hotel_name}"`, h.city,
      h.check_in, h.check_out, h.room_type, h.booking_amount, h.vendor_name, h.status
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Hotel_Bookings_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredHotels = hotels.filter((h) => {
    const q = searchTerm.toLowerCase();
    return (
      (h.booking_ref || '').toLowerCase().includes(q) ||
      (h.employee_name || '').toLowerCase().includes(q) ||
      (h.hotel_name || '').toLowerCase().includes(q) ||
      (h.city || '').toLowerCase().includes(q) ||
      (h.vendor_name || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
            <Building2 className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Hotel Reservations &amp; Stay Desk</h1>
            <p className="text-xs text-blue-200">
              Corporate Accommodation · Partner Hotel Chains · Check-in / Check-out &amp; Invoice Billing.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={exportCSV}
            className="px-3.5 py-2 bg-white/10 hover:bg-white/20 text-white font-bold text-xs rounded-xl flex items-center gap-1.5 transition-all"
          >
            <Download className="w-3.5 h-3.5" /> Export
          </button>

          {hasPermission(user, 'travel_desk.hotel_booking.edit') && (
            <button
              onClick={() => {
                setEditHotel(null);
                setFormData(initialForm);
                setShowCreateModal(true);
              }}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-xl shadow-md flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" /> Book New Hotel
            </button>
          )}
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search ref #, employee, hotel, city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs outline-none focus:border-blue-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-1 text-xs text-slate-600 font-bold">
            <Filter className="w-3.5 h-3.5" /> Status:
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold outline-none"
          >
            <option value="all">All Statuses</option>
            <option value="requested">Requested</option>
            <option value="approved">Approved</option>
            <option value="booked">Booked</option>
            <option value="voucher_generated">Voucher Generated</option>
            <option value="stay_completed">Stay Completed</option>
            <option value="invoice_linked">Invoice Linked</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <button
            onClick={fetchHotels}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-slate-600 transition-colors"
            title="Refresh List"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-xs text-slate-800 flex justify-between">
          <span>Corporate Hotel Stay Register ({filteredHotels.length})</span>
          <span className="text-slate-400">Live Database Master</span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500 font-medium">Loading hotel bookings...</div>
        ) : filteredHotels.length === 0 ? (
          <div className="p-12 text-center text-slate-400 font-semibold italic">
            No hotel bookings found matching your filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px]">
                <tr>
                  <th className="p-3.5">Booking Ref</th>
                  <th className="p-3.5">Employee</th>
                  <th className="p-3.5">Hotel &amp; City</th>
                  <th className="p-3.5">Check-in / Check-out</th>
                  <th className="p-3.5">Room &amp; Meals</th>
                  <th className="p-3.5">Vendor</th>
                  <th className="p-3.5 text-right">Stay Cost (₹)</th>
                  <th className="p-3.5">Status</th>
                  <th className="p-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                {filteredHotels.map((h) => (
                  <tr key={h._id} className="hover:bg-slate-50">
                    <td className="p-3.5 font-mono font-bold text-blue-600">{h.booking_ref}</td>
                    <td className="p-3.5 font-bold">
                      <div>{h.employee_name}</div>
                      <div className="text-[10px] text-slate-400 font-normal">{h.department}</div>
                    </td>
                    <td className="p-3.5">
                      <div className="font-bold text-slate-900">{h.hotel_name}</div>
                      <div className="text-slate-500 text-[10px]">{h.city}</div>
                    </td>
                    <td className="p-3.5 font-semibold text-slate-700">
                      <div><b>In:</b> {h.check_in}</div>
                      <div className="text-slate-500"><b>Out:</b> {h.check_out}</div>
                    </td>
                    <td className="p-3.5">
                      <div>{h.room_type} ({h.number_of_rooms} Room)</div>
                      <div className="text-[10px] text-emerald-600 font-semibold">{h.meals}</div>
                    </td>
                    <td className="p-3.5">{h.vendor_name}</td>
                    <td className="p-3.5 font-mono font-bold text-emerald-700 text-right">
                      ₹ {(h.booking_amount || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="p-3.5">
                      <span className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase ${
                        h.status === 'voucher_generated' || h.status === 'stay_completed'
                          ? 'bg-emerald-100 text-emerald-800'
                          : h.status === 'booked' || h.status === 'approved'
                          ? 'bg-blue-100 text-blue-800'
                          : h.status === 'cancelled'
                          ? 'bg-rose-100 text-rose-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {h.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="p-3.5 text-right space-x-1 shrink-0">
                      <button
                        onClick={() => setVoucherHotel(h)}
                        className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg font-bold text-[11px]"
                        title="View Voucher & Details"
                      >
                        Voucher
                      </button>
                      <button
                        onClick={() => setWorkflowHotel(h)}
                        className="p-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg font-bold text-[11px]"
                        title="Workflow Progression"
                      >
                        Workflow
                      </button>
                      {hasPermission(user, 'travel_desk.hotel_booking.edit') && (
                        <button
                          onClick={() => openEdit(h)}
                          className="p-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg font-bold text-[11px]"
                          title="Edit"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteHotel(h)}
                        className="p-1.5 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-lg font-bold text-[11px]"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CREATE / EDIT MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-500" />
                {editHotel ? `Edit Hotel Booking (${editHotel.booking_ref})` : 'Create Hotel Booking Requisition'}
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 font-bold">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveHotel} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Employee Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.employee_name}
                    onChange={(e) => setFormData({ ...formData, employee_name: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    placeholder="Anita Roy"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Hotel Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="Taj Yeshwantpur"
                    value={formData.hotel_name}
                    onChange={(e) => setFormData({ ...formData, hotel_name: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">City *</label>
                  <input
                    type="text"
                    required
                    placeholder="Bengaluru"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Check-in Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.check_in}
                    onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-semibold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Check-out Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.check_out}
                    onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-semibold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Rooms</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.number_of_rooms}
                    onChange={(e) => setFormData({ ...formData, number_of_rooms: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Guests</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.guests}
                    onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Room Type</label>
                  <input
                    type="text"
                    value={formData.room_type}
                    onChange={(e) => setFormData({ ...formData, room_type: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    placeholder="Deluxe Executive King"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Meals Option</label>
                  <select
                    value={formData.meals}
                    onChange={(e) => setFormData({ ...formData, meals: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  >
                    <option value="Breakfast Included">Breakfast Included</option>
                    <option value="Breakfast & Lunch">Breakfast &amp; Lunch</option>
                    <option value="Breakfast & Dinner">Breakfast &amp; Dinner</option>
                    <option value="Room Only">Room Only (No Meals)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Corporate Nightly Rate (₹)</label>
                  <input
                    type="number"
                    value={formData.corporate_rate}
                    onChange={(e) => setFormData({ ...formData, corporate_rate: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Total Stay Amount (₹)</label>
                  <input
                    type="number"
                    value={formData.booking_amount}
                    onChange={(e) => setFormData({ ...formData, booking_amount: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono font-bold text-emerald-700"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Vendor Partner</label>
                  <input
                    type="text"
                    value={formData.vendor_name}
                    onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                    placeholder="Taj Hotels"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Remarks &amp; Inclusions</label>
                <textarea
                  rows="2"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-xl outline-none text-xs"
                  placeholder="Special requests e.g. Early check-in or airport shuttle."
                />
              </div>

              <div className="pt-3 border-t border-slate-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-slate-100 font-bold text-xs rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-md"
                >
                  {editHotel ? 'Save Changes' : 'Confirm Hotel Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* WORKFLOW ACTION MODAL */}
      {workflowHotel && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-sm">
                Workflow Actions — {workflowHotel.booking_ref}
              </h3>
              <button onClick={() => setWorkflowHotel(null)} className="text-slate-400 font-bold">&times;</button>
            </div>

            <div className="space-y-2 pt-2">
              <button
                onClick={() => handleWorkflowAction(workflowHotel._id, 'approve')}
                className="w-full py-2 bg-emerald-600 text-white font-bold text-xs rounded-xl hover:bg-emerald-500"
              >
                1. Approve Hotel Request
              </button>
              <button
                onClick={() => handleWorkflowAction(workflowHotel._id, 'book')}
                className="w-full py-2 bg-blue-600 text-white font-bold text-xs rounded-xl hover:bg-blue-500"
              >
                2. Mark as Booked
              </button>
              <button
                onClick={() => handleWorkflowAction(workflowHotel._id, 'generate_voucher', { voucher_ref: `VCH-${workflowHotel.booking_ref}` })}
                className="w-full py-2 bg-indigo-600 text-white font-bold text-xs rounded-xl hover:bg-indigo-500"
              >
                3. Generate Hotel Voucher
              </button>
              <button
                onClick={() => handleWorkflowAction(workflowHotel._id, 'complete_stay')}
                className="w-full py-2 bg-purple-600 text-white font-bold text-xs rounded-xl hover:bg-purple-500"
              >
                4. Mark Stay Completed
              </button>
              <button
                onClick={() => handleWorkflowAction(workflowHotel._id, 'cancel')}
                className="w-full py-2 bg-rose-600 text-white font-bold text-xs rounded-xl hover:bg-rose-500"
              >
                Cancel Booking
              </button>
            </div>

            <div className="pt-2 border-t border-slate-100 flex justify-end">
              <button onClick={() => setWorkflowHotel(null)} className="px-4 py-2 bg-slate-100 text-xs font-bold rounded-xl">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VOUCHER / DETAIL MODAL */}
      {voucherHotel && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" /> Corporate Stay Voucher
              </h3>
              <button onClick={() => setVoucherHotel(null)} className="text-slate-400 font-bold">&times;</button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white p-4 rounded-xl space-y-1">
                <div className="text-amber-400 font-mono text-xs font-bold">VOUCHER REF: {voucherHotel.voucher_ref || 'VCH-PENDING'}</div>
                <div className="text-lg font-black">{voucherHotel.hotel_name}</div>
                <div className="text-blue-200">{voucherHotel.city}</div>
              </div>

              <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded-xl border border-slate-200">
                <div>
                  <span className="text-slate-400 font-medium block">Guest Name</span>
                  <span className="font-bold text-slate-900">{voucherHotel.employee_name}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block">Department</span>
                  <span className="font-bold text-slate-900">{voucherHotel.department}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block">Check-in</span>
                  <span className="font-bold text-emerald-600">{voucherHotel.check_in}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block">Check-out</span>
                  <span className="font-bold text-rose-600">{voucherHotel.check_out}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block">Room &amp; Meals</span>
                  <span className="font-bold text-slate-900">{voucherHotel.room_type} · {voucherHotel.meals}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium block">Stay Amount</span>
                  <span className="font-mono font-bold text-emerald-700">₹ {(voucherHotel.booking_amount || 0).toLocaleString('en-IN')}</span>
                </div>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl space-y-1">
                <span className="text-amber-900 font-bold">Vendor Terms &amp; Instructions</span>
                <p className="text-slate-600 text-[11px]">{voucherHotel.remarks || 'Present this corporate voucher at front desk along with employee ID card.'}</p>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100 flex justify-end">
              <button onClick={() => setVoucherHotel(null)} className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-xl">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SOFT DELETE MODAL */}
      {deleteHotel && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-bold text-rose-600 text-base">Confirm Delete</h3>
            <p className="text-xs text-slate-600">
              Are you sure you want to delete Hotel Booking <b className="font-mono text-slate-900">{deleteHotel.booking_ref}</b>?
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDeleteHotel(null)} className="px-4 py-2 bg-slate-100 text-xs font-bold rounded-xl">
                Cancel
              </button>
              <button onClick={() => handleDeleteHotel(deleteHotel._id)} className="px-5 py-2 bg-rose-600 text-white text-xs font-bold rounded-xl">
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelHotelBooking;
